import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { doc, getDoc, setDoc } from '../lib/offlineFirestore';
import { db, storage } from '../config/firebase';

const DB_NAME = 'coldstorage-offline-files-v1';
const DB_VERSION = 1;
const STORE = 'attachments';

const openDb = () => new Promise((resolve, reject) => {
  if (typeof window === 'undefined' || !window.indexedDB) return resolve(null);
  const request = window.indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE)) {
      const store = database.createObjectStore(STORE, { keyPath: 'id' });
      store.createIndex('status', 'status', { unique: false });
      store.createIndex('inwardId', 'inwardId', { unique: false });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const list = async () => {
  const database = await openDb().catch(() => null);
  if (!database) return [];
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE, 'readonly');
    const request = tx.objectStore(STORE).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => database.close();
  });
};

const put = async (row) => {
  const database = await openDb().catch(() => null);
  if (!database) throw new Error('IndexedDB is unavailable; inspection photo cannot be queued offline.');
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(row);
    tx.oncomplete = () => { database.close(); resolve(row); };
    tx.onerror = () => { database.close(); reject(tx.error); };
  });
};

const remove = async (id) => {
  const database = await openDb().catch(() => null);
  if (!database) return;
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => { database.close(); resolve(); };
    tx.onerror = () => { database.close(); reject(tx.error); };
  });
};

const appendUrl = (record = {}, varietyIndex, url) => {
  const append = (arr) => Array.from(new Set([...(Array.isArray(arr) ? arr : []), url]));
  const varietyGroups = Array.isArray(record.varietyGroups) ? record.varietyGroups.map((row, index) => index === varietyIndex ? { ...row, imageUrls: append(row.imageUrls) } : row) : [];
  const unloadingGroups = Array.isArray(record.unloadingRecord?.varietyGroups) ? record.unloadingRecord.varietyGroups.map((row, index) => index === varietyIndex ? { ...row, imageUrls: append(row.imageUrls) } : row) : varietyGroups;
  const inspectionGroups = Array.isArray(record.inspectionRecord?.varietyGroups) ? record.inspectionRecord.varietyGroups.map((row, index) => index === varietyIndex ? { ...row, imageUrls: append(row.imageUrls) } : row) : varietyGroups;
  return {
    varietyGroups,
    inspectionImageUrls: append(record.inspectionImageUrls),
    unloadingRecord: { ...(record.unloadingRecord || {}), varietyGroups: unloadingGroups },
    inspectionRecord: {
      ...(record.inspectionRecord || {}),
      varietyGroups: inspectionGroups,
      imageUrls: append(record.inspectionRecord?.imageUrls),
      photos: append(record.inspectionRecord?.photos),
    },
  };
};

const updateInwardAttachment = async ({ tenantId, inwardId, varietyIndex, url }) => {
  const refs = [doc(db, 'inwards', inwardId)];
  if (tenantId) refs.push(doc(db, 'tenants', tenantId, 'inwards', inwardId));
  let updated = false;
  for (const inwardRef of refs) {
    try {
      const snap = await getDoc(inwardRef);
      if (!snap.exists()) continue;
      await setDoc(inwardRef, { ...appendUrl(snap.data() || {}, varietyIndex, url), updatedAt: new Date().toISOString() }, { merge: true });
      updated = true;
    } catch (_) {}
  }
  if (!updated) throw new Error('The related Inward could not be found while attaching an offline inspection photo.');
};

const updateDocumentAttachment = async ({ collectionName, documentId, fieldNames = [], url }) => {
  if (!collectionName || !documentId || !fieldNames.length) throw new Error('Offline attachment target is incomplete.');
  const updates = { attachmentSyncedAt: new Date().toISOString() };
  fieldNames.forEach((field) => { if (field) updates[field] = url; });
  if (fieldNames.some((field) => /^selfie/i.test(field))) updates.selfieUploadPending = false;
  await setDoc(doc(db, collectionName, documentId), updates, { merge: true });
};

const offlineAttachmentService = {
  async queueInspectionFile({ tenantId, inwardId, varietyIndex = 0, storagePath, file }) {
    if (!file) return null;
    const id = `ATT-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    await put({
      id,
      tenantId: String(tenantId || ''),
      inwardId: String(inwardId || ''),
      varietyIndex: Number(varietyIndex || 0),
      storagePath: String(storagePath || ''),
      file,
      fileName: file.name || 'inspection.jpg',
      contentType: file.type || 'image/jpeg',
      status: 'pending',
      createdAt: new Date().toISOString(),
      error: '',
    });
    return id;
  },

  async queueDocumentFile({ tenantId, collectionName, documentId, fieldNames = [], storagePath, file }) {
    if (!file || !collectionName || !documentId) return null;
    const id = `DOCATT-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    await put({
      id,
      kind: 'document',
      tenantId: String(tenantId || ''),
      collectionName: String(collectionName),
      documentId: String(documentId),
      fieldNames: Array.isArray(fieldNames) ? fieldNames.filter(Boolean) : [String(fieldNames || '')].filter(Boolean),
      storagePath: String(storagePath || ''),
      file,
      fileName: file.name || 'attachment.jpg',
      contentType: file.type || 'image/jpeg',
      status: 'pending',
      createdAt: new Date().toISOString(),
      error: '',
    });
    return id;
  },

  async countPending() {
    const rows = await list();
    return rows.filter((row) => row.status !== 'synced').length;
  },

  async processQueue() {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return { uploaded: 0, failed: 0 };
    const rows = await list();
    let uploaded = 0;
    let failed = 0;
    for (const row of rows) {
      try {
        await put({ ...row, status: 'uploading', error: '' });
        const target = ref(storage, row.storagePath);
        const snapshot = await uploadBytes(target, row.file, { contentType: row.contentType || 'image/jpeg' });
        const url = await getDownloadURL(snapshot.ref);
        if (row.kind === 'document') {
          await updateDocumentAttachment({ collectionName: row.collectionName, documentId: row.documentId, fieldNames: row.fieldNames, url });
        } else {
          await updateInwardAttachment({ tenantId: row.tenantId, inwardId: row.inwardId, varietyIndex: row.varietyIndex, url });
        }
        await remove(row.id);
        uploaded += 1;
      } catch (error) {
        failed += 1;
        await put({ ...row, status: 'failed', error: String(error?.message || error || 'Upload failed'), updatedAt: new Date().toISOString() }).catch(() => {});
      }
    }
    return { uploaded, failed };
  },
};

export default offlineAttachmentService;
